import React from 'react'
import { Fragment, useEffect } from 'react'
import { fattPayClient } from "./FattPayClient";
import { Col, Form, Row, Button, Image } from 'react-bootstrap';
import Payment, { BankHolderType, BankType } from './Payment';
import { deepCopy } from '../../Components/Shared/Utility/DeepCopy';
import { ValidatorHelper } from '../../Components/Shared/ValidatorHelper';

interface IProps {
    onPaymentChanged: (payment: Payment) => void;
    payment: Payment,
    disabled?: boolean
}

export const FattPayBank =  React.forwardRef((props: IProps, ref: React.Ref<any>) => {
    var routingNumberRef = React.useRef(null);
    var accountNumberRef = React.useRef(null);
    var reenterAccountNumberRef = React.useRef(null);

    React.useImperativeHandle(ref,
        () => ({
            validate(payment: Payment) : boolean {
                var isValid = true;
                ValidatorHelper.clearCustomValidation(routingNumberRef.current);
                ValidatorHelper.clearCustomValidation(accountNumberRef.current);
                ValidatorHelper.clearCustomValidation(reenterAccountNumberRef.current);

                if(payment.routingNumber === undefined || payment.routingNumber.length < 9) {
                    ValidatorHelper.setCustomValidationWithFeedback(routingNumberRef.current, "9-digit routing number is Required");
                    isValid = false;
                }

                if(payment.accountNumber !== payment.secondAccountNumber) {
                    ValidatorHelper.setCustomValidationWithFeedback(accountNumberRef.current, "Account numbers do not match");
                    ValidatorHelper.setCustomValidationWithFeedback(reenterAccountNumberRef.current, "Account numbers do not match");
                    isValid = false;
                }
                
                return isValid;
            }
        }),
    );

    useEffect(() => {
        async function initialize() {
            //Initialize Bank
            await fattPayClient.initializeBank();
        }
        initialize();
    }, []);

    const updateAccountNumber = (accountNumber: string) => {
        const updated = deepCopy(props.payment);
        updated.accountNumber = accountNumber;
        props.onPaymentChanged(updated);
        if(updated.accountNumber !== updated.secondAccountNumber) {
            ValidatorHelper.setCustomValidationWithFeedback(accountNumberRef.current, "Account numbers do not match");
            ValidatorHelper.setCustomValidationWithFeedback(reenterAccountNumberRef.current, "Account numbers do not match");
        }
        if(updated.secondAccountNumber === updated.accountNumber) {
            ValidatorHelper.clearCustomValidation(accountNumberRef.current);
            ValidatorHelper.clearCustomValidation(reenterAccountNumberRef.current);
        }
    }

    const updateSecondAccountNumber = (accountNumber: string) => {
        const updated = deepCopy(props.payment);
        updated.secondAccountNumber = accountNumber;
        props.onPaymentChanged(updated);
        
        if(updated.secondAccountNumber !== updated.accountNumber) {
            ValidatorHelper.setCustomValidationWithFeedback(accountNumberRef.current, "Account numbers do not match");
            ValidatorHelper.setCustomValidationWithFeedback(reenterAccountNumberRef.current, "Account numbers do not match");
        }
        if(updated.secondAccountNumber === updated.accountNumber) {
            ValidatorHelper.clearCustomValidation(accountNumberRef.current);
            ValidatorHelper.clearCustomValidation(reenterAccountNumberRef.current);
        }
    }

    const updateRoutingNumber = (routingNumber: string) => {
        const updated = deepCopy(props.payment);
        updated.routingNumber = routingNumber;
        props.onPaymentChanged(updated);
        if(updated.routingNumber === undefined || updated.routingNumber.length < 9) {
            ValidatorHelper.setCustomValidationWithFeedback(routingNumberRef.current, "9-digit routing number is Required");
        }
        if(routingNumber.length === 9) {
            ValidatorHelper.clearCustomValidation(routingNumberRef.current);
        }
    }
    
    const updateBankType = (type: BankType) => {
        const updated = deepCopy(props.payment);
        updated.bankType = type;
        props.onPaymentChanged(updated);
    }
    
    const updateBankHolderType = (type: BankHolderType) => {
        const updated = deepCopy(props.payment);
        updated.bankHolderType = type;
        props.onPaymentChanged(updated);
    }

    const updateFirstName = (firstName: string) => {
        const updated = deepCopy(props.payment);
        updated.firstName = firstName;
        props.onPaymentChanged(updated);
    }

    const updateLastName = (lastName: string) => {
        const updated = deepCopy(props.payment);
        updated.lastName = lastName;
        props.onPaymentChanged(updated);
    }

    return (
        <Fragment>
            <Form.Group className="mt-4">
                <Form.Label className="font-weight-bolder mb-1 pp-font-size-smaller">Account Holder Name
                    <span className="text-pp-primary ml-2">*</span></Form.Label>
                <Form.Row>
                    <Col>
                        <Form.Control type="input" required placeholder="First" isInvalid={props.payment.error?.field === "first_name"}
                            onChange={(e) => updateFirstName(e.target.value)}
                            defaultValue={props.payment.firstName} />
                        <Form.Control.Feedback type="invalid">First Name is Required</Form.Control.Feedback>
                    </Col>
                    <Col>
                        <Form.Control type="input" required placeholder="Last" isInvalid={props.payment.error?.field === "last_name"}
                            onChange={(e) => updateLastName(e.target.value)}
                            defaultValue={props.payment.lastName} />
                        <Form.Control.Feedback type="invalid">Last Name is Required</Form.Control.Feedback>
                    </Col>
                </Form.Row>
            </Form.Group>
            <Form.Group className="mt-4">
                <Form.Row>   
                    <Col xs={12} md={5} lg={6}>
                        <Form.Row>
                            <Col>
                                <Form.Label className="font-weight-bolder mb-1 pp-font-size-smaller">Routing Number
                                    <span className="text-pp-primary ml-2">*</span></Form.Label>
                                <Form.Control ref={routingNumberRef} type="input" required style={{ height: "calc(1.5em + 0.75rem + 2px)" }} isInvalid={props.payment.error?.field === "routing_number"}
                                    onChange={(e) => updateRoutingNumber(e.target.value)}
                                    defaultValue={props.payment.routingNumber}
                                    maxLength={9} />
                                <Form.Control.Feedback type="invalid">9-digit routing number is Required</Form.Control.Feedback>
                            </Col>
                        </Form.Row>
                        <Form.Row className="mt-4">
                            <Col>
                                <Form.Label className="font-weight-bolder mb-1 pp-font-size-smaller">Account Number
                                    <span className="text-pp-primary ml-2">*</span></Form.Label>
                                <Form.Control ref={accountNumberRef} type="input" required
                                    onChange={(e) => updateAccountNumber(e.target.value)}
                                    defaultValue={props.payment.accountNumber} />
                                <span className="invalid-feedback">Account numbers do not match</span>
                            </Col>
                        </Form.Row>
                        <Form.Row className="mt-4">
                            <Col>
                                <Form.Label className="font-weight-bolder mb-1 pp-font-size-smaller">Re-Enter Account Number
                                    <span className="text-pp-primary ml-2">*</span></Form.Label>
                                <Form.Control ref={reenterAccountNumberRef} type="input" required
                                    onChange={(e) => updateSecondAccountNumber(e.target.value)}
                                    defaultValue={""} />
                                <span className="invalid-feedback">Account numbers do not match</span>
                            </Col>
                        </Form.Row>
                    </Col>
                    <Col xs={12} md={5} lg={6}>
                        <Image style={{ height: "auto", width: "100%", maxWidth: "350px", maxHeight:"200px", marginTop: "1.5rem" }} src="/physicianportal/ABPNCheck.png" />
                    </Col>
                </Form.Row>
            </Form.Group>
            <Form.Group controlId="bankType">
                <Row>
                    <Col xs={4} md={5} lg={4}>
                        <Form.Label className="font-weight-bolder mb-1 pp-font-size-smaller">Type<span className="text-pp-primary ml-2">*</span></Form.Label>
                        <Form.Check
                            type="radio"
                            label="Checking"
                            formNoValidate={true}
                            checked={props.payment.bankType == BankType.checking}
                            onChange={() => updateBankType(BankType.checking)}
                            className="ml-1 mt-3" />
                    </Col>
                    <Col xs={4} md={5} lg={4}>
                        <Form.Label></Form.Label>
                        <Form.Check
                            type="radio"
                            formNoValidate={true}
                            label="Savings"
                            checked={props.payment.bankType == BankType.savings}
                            onChange={() => updateBankType(BankType.savings)}
                            className="ml-1 mt-3" />
                    </Col>
                </Row>
            </Form.Group>
            <Form.Group controlId="bankHolderType">
                <Row>
                    <Col xs={4} md={5} lg={4}>
                        <Form.Label className="font-weight-bolder mb-1 pp-font-size-smaller">Purpose<span className="text-pp-primary ml-2">*</span></Form.Label>
                        <Form.Check
                            type="radio"
                            label="Personal"
                            checked={props.payment.bankHolderType == BankHolderType.personal}
                            onChange={() => updateBankHolderType(BankHolderType.personal)}
                            className="ml-1 mt-3" />
                    </Col>
                    <Col xs={4} md={5} lg={4}>
                        <Form.Label></Form.Label>
                        <Form.Check
                            type="radio"
                            label="Business"
                            checked={props.payment.bankHolderType == BankHolderType.business}
                            onChange={() => updateBankHolderType(BankHolderType.business)}
                            className="ml-1 mt-3" />
                    </Col>
                </Row>
            </Form.Group>

            <Button type="submit"
                className="mt-4"
                variant="pp-primary"
                disabled={props.disabled}>
                Submit Payment
            </Button>

        </Fragment>);
});